import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Loader } from 'lucide-react';
import { Form, FormField } from 'components/ui/form';
import { InputGs, SelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import type { ViagensBillingViagensFilters } from '../../types';
import { useViagensBillingSearchParams } from 'hooks/store/viagens-billing';

type Props = {
  onSubmitFilters: (filters: ViagensBillingViagensFilters) => void;
  onResetFilters: () => void;
  filterLoadingAction?: 'filter' | 'reset' | null;
};

type FormData = {
  idViagem: string;
  placa: string;
  notas: string;
  agendamentoInicio: string;
  agendamentoFim: string;
  approvalStatus: string;
};

const approvalStatusOptions = [
  { name: 'Todas', value: 'all' },
  { name: 'Aprovada', value: 'approved' },
  { name: 'Reprovada', value: 'rejected' },
];

export const ViagensBillingViagensFilter = ({
  onSubmitFilters,
  onResetFilters,
  filterLoadingAction,
}: Props) => {
  const { viagensFilters } = useViagensBillingSearchParams({ persisted: true });
  const form = useForm<FormData>({
    defaultValues: {
      idViagem: viagensFilters.idViagem ?? '',
      placa: viagensFilters.placa ?? '',
      notas: viagensFilters.notas ?? '',
      agendamentoInicio: viagensFilters.agendamentoInicio ?? '',
      agendamentoFim: viagensFilters.agendamentoFim ?? '',
      approvalStatus: viagensFilters.approvalStatus ?? 'all',
    },
  });

  const buildFilters = React.useCallback((formData: FormData) => {
    return {
      idViagem: formData.idViagem?.trim() || undefined,
      placa: formData.placa?.trim() || undefined,
      notas: formData.notas?.trim() || undefined,
      agendamentoInicio: formData.agendamentoInicio || undefined,
      agendamentoFim: formData.agendamentoFim || undefined,
      approvalStatus:
        formData.approvalStatus === 'all'
          ? undefined
          : (formData.approvalStatus as ViagensBillingViagensFilters['approvalStatus']),
    };
  }, []);

  const resetFilters = React.useCallback(() => {
    form.reset({
      idViagem: '',
      placa: '',
      notas: '',
      agendamentoInicio: '',
      agendamentoFim: '',
      approvalStatus: 'all',
    });
    onResetFilters();
  }, [form, onResetFilters]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) =>
          onSubmitFilters(buildFilters(data))
        )}
        className="w-full"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
          <FormField
            control={form.control}
            name="idViagem"
            render={({ field }) => (
              <InputGs {...field} type="text" label="ID viagem" />
            )}
          />
          <FormField
            control={form.control}
            name="placa"
            render={({ field }) => (
              <InputGs {...field} type="text" label="Placa" />
            )}
          />
          <FormField
            control={form.control}
            name="notas"
            render={({ field }) => (
              <InputGs {...field} type="text" label="Nota" />
            )}
          />
          <FormField
            control={form.control}
            name="agendamentoInicio"
            render={({ field }) => (
              <InputGs {...field} type="date" label="Agendamento de" />
            )}
          />
          <FormField
            control={form.control}
            name="agendamentoFim"
            render={({ field }) => (
              <InputGs {...field} type="date" label="Agendamento até" />
            )}
          />
          <FormField
            control={form.control}
            name="approvalStatus"
            render={({ field }) => (
              <SelectGs
                {...field}
                onValueChange={field.onChange}
                label="Aprovação"
                content={approvalStatusOptions}
              />
            )}
          />
        </div>

        <div className="flex items-center mt-6 gap-4">
          <Button.Root
            type="button"
            size="sm"
            variant="secondary"
            disabled={Boolean(filterLoadingAction)}
            onClick={resetFilters}
          >
            <span className="relative grid place-items-center">
              <span
                className={filterLoadingAction === 'reset' ? 'invisible' : ''}
              >
                Limpar filtros
              </span>
              {filterLoadingAction === 'reset' && (
                <Loader className="absolute h-4 w-4 animate-spin" />
              )}
            </span>
          </Button.Root>

          <Button.Root
            type="submit"
            size="sm"
            variant="primary"
            disabled={Boolean(filterLoadingAction)}
          >
            <span className="relative grid place-items-center">
              <span
                className={filterLoadingAction === 'filter' ? 'invisible' : ''}
              >
                Filtrar
              </span>
              {filterLoadingAction === 'filter' && (
                <Loader className="absolute h-4 w-4 animate-spin" />
              )}
            </span>
          </Button.Root>
        </div>
      </form>
    </Form>
  );
};
